// KPIs over the listings currently loaded on the map.
// Only listings with a matched sale count. A sale dated before the listing date belongs to an earlier sale of the
// property, not to this listing, so those matches are excluded from every KPI.

const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);
const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};
const summarise = (values) => ({ mean: mean(values), median: median(values), n: values.length });

export function computeKpis(listings) {
  const sold = listings.filter((l) => l.price_sold != null);
  const valid = sold.filter((l) => l.days_on_market == null || l.days_on_market >= 0);
  const numbers = (field) => valid.map((l) => l[field]).filter((v) => v != null);

  return {
    soldCount: valid.length,
    excluded: sold.length - valid.length,
    soldPrice: summarise(numbers('price_sold')),
    daysOnMarket: summarise(numbers('days_on_market')),
    upliftEur: summarise(numbers('price_difference')),
    upliftPct: summarise(numbers('price_difference_pct'))
  };
}

// Same exclusion and statistics as computeKpis, grouped by year of sale (newest first)
export function computeKpisByYear(listings) {
  const byYear = {};
  listings.forEach((l) => {
    if (l.price_sold == null || !l.date_sold) return;
    if (l.days_on_market != null && l.days_on_market < 0) return;
    (byYear[String(l.date_sold).slice(0, 4)] ||= []).push(l);
  });
  return Object.keys(byYear)
    .sort((a, b) => b - a)
    .map((year) => {
      const group = byYear[year];
      const numbers = (field) => group.map((l) => l[field]).filter((v) => v != null);
      return {
        year,
        count: group.length,
        soldPrice: mean(numbers('price_sold')),
        daysOnMarket: mean(numbers('days_on_market')),
        upliftEur: mean(numbers('price_difference')),
        upliftPct: mean(numbers('price_difference_pct'))
      };
    });
}

const euro = (value, signed = false) => {
  if (value == null) return '—';
  const text = Math.abs(Math.round(value)).toLocaleString('en-IE');
  const sign = value < 0 ? '-' : signed && Math.round(value) > 0 ? '+' : '';
  return `${sign}€${text}`;
};

const percent = (value) => (value == null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(1)}%`);

// Same wording as the listing cards (30-day months)
const duration = (days) => {
  if (days == null) return '—';
  const whole = Math.round(days);
  const months = Math.floor(whole / 30);
  const rest = whole % 30;
  const parts = [];
  if (months > 0) parts.push(`${months} Month${months > 1 ? 's' : ''}`);
  if (rest > 0) parts.push(`${rest} day${rest > 1 ? 's' : ''}`);
  return parts.length ? parts.join(' ') : 'Less than a day';
};

// Compact forms for the by-year table: +€116k, 5 mo
const euroK = (value, signed = false) => {
  if (value == null) return '—';
  const k = Math.round(value / 1000);
  return `${k < 0 ? '-' : signed && k > 0 ? '+' : ''}€${Math.abs(k).toLocaleString('en-IE')}k`;
};
const months = (days) => (days == null ? '—' : `${Math.round(days / 30)} mo`);

export const format = { euro, euroK, percent, duration, months };
